import React, { useRef, useEffect, type ReactNode } from 'react';
import createFocusTrap from 'focus-trap';

import stylesheet from './FocusTrap.css';

export type FocusTrapProps = {
  children: ReactNode;
  disabled?: boolean;
};

function FocusTrap({ children, disabled = false }: FocusTrapProps) {
  const focusTrapRef = useRef<ReturnType<typeof createFocusTrap> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && !disabled) {
      focusTrapRef.current = createFocusTrap(containerRef.current, {
        clickOutsideDeactivates: false,
        escapeDeactivates: false, // Let the component handle its own closing logic
        initialFocus: undefined, // Don't auto-focus at all
        returnFocusOnDeactivate: true,
      });

      try {
        focusTrapRef.current.activate();
      } catch (error) {
        // If activation fails (e.g., no focusable elements), just log and continue
        console.warn('Focus trap activation failed:', error);
      }
    } else if (disabled && focusTrapRef.current) {
      // Only clean up when transitioning from enabled to disabled
      try {
        focusTrapRef.current.deactivate();
      } catch (error) {
        console.warn('Focus trap deactivation failed:', error);
      }
    }

    // Only cleanup on unmount
    return () => {
      if (focusTrapRef.current) {
        try {
          focusTrapRef.current.deactivate();
        } catch (error) {
          console.warn('Focus trap deactivation failed:', error);
        }
      }
    };
  }, [disabled]);

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <div ref={containerRef} className={stylesheet.wrapper}>
      {children}
    </div>
  );
}

export default FocusTrap;
